/**
 * hooks/useDynamicFavicon.ts — Generates and updates the browser tab favicon
 * based on the currently selected theme and light/dark mode.
 *
 * WHY THIS EXISTS:
 *   A static favicon.png doesn't change with the theme, so it can look out of
 *   place when the user selects a dark or colorful theme. This hook draws a
 *   tiny clock face onto an HTML5 <canvas>, converts it to a PNG data URL, and
 *   injects (or replaces) a *dedicated* <link rel="icon"> tag in <head> —
 *   giving the browser tab a live, theme-matched icon without any static
 *   image files.
 *
 *   IMPORTANT: this hook must never touch the static
 *   `<link rel="icon" href="/favicon.png">` declared in public/index.html.
 *   Google's favicon indexer fetches that tag directly and requires a real,
 *   stable, crawlable image URL — it does not accept `data:` URIs. If this
 *   hook overwrote that tag (as it used to), Google would see only the
 *   inline data URI after rendering the page and fail to index any favicon,
 *   which is why search results showed a generic globe icon site-wide. So
 *   the hook targets its own `data-dynamic-favicon` link instead, appended
 *   after the static one — browsers use the last-declared icon for the tab,
 *   while crawlers that don't run JS (or fail to) still see the static one.
 *
 * HOW TO USE:
 *   Call this hook once at the top of a component that owns theme state:
 *     useDynamicFavicon(themeId, mode);
 *   It has no return value — it only produces the side effect of updating the
 *   favicon whenever `themeId` or `mode` changes.
 *
 * CANVAS DRAWING ORDER (each step builds on the previous):
 *   1. Background circle  — filled with bgSecondary color
 *   2. Clock ring         — stroked circle outline using accent color
 *   3. Tick marks         — 4 short lines at 12/3/6/9 positions
 *   4. Hour hand          — thick line pointing toward 10 o'clock
 *   5. Minute hand        — thinner line pointing toward 2 o'clock
 *   6. Center dot         — small filled circle at the clock's pivot point
 *
 * The clock is always drawn showing "10:10" — a classic industry convention
 * (watches in ads show 10:10 because it makes the face look symmetrical
 * and happy). It does NOT show the real time; it is purely a brand icon.
 */

import { useEffect } from 'react';
import { themes } from '../constants/themes';

/**
 * @param themeId - The numeric ID of the currently selected theme (0–9).
 * @param mode    - "light" or "dark" — selects which palette to draw with.
 */
export function useDynamicFavicon(themeId: number, mode: string): void {
  useEffect(() => {
    // Look up the selected theme object from the themes array.
    const selected = themes.find(t => t.id === themeId);
    if (!selected) return; // Unknown themeId — nothing to draw.

    // Choose the light or dark color palette based on the current mode.
    const palette = mode === 'dark' ? selected.dark : selected.light;

    // Create an off-screen 32×32 canvas (the standard favicon size).
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');
    if (!ctx) return; // Canvas API not supported — exit silently.

    // Clock geometry: center (cx, cy) and radius (r).
    const cx = 16, cy = 16, r = 12;

    // ── Step 1: Background circle ─────────────────────────────────────────────
    // Draw a slightly larger circle (r+3) filled with the secondary background
    // color to give the clock a clean background disc.
    ctx.beginPath();
    ctx.arc(cx, cy, r + 3, 0, Math.PI * 2);
    ctx.fillStyle = palette.bgSecondary;
    ctx.fill();

    // ── Step 2: Clock ring ────────────────────────────────────────────────────
    // Stroke a circle at radius r to form the outer edge of the clock face.
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.strokeStyle = palette.accent;
    ctx.lineWidth = 2;
    ctx.stroke();

    // ── Step 3: Cardinal tick marks ───────────────────────────────────────────
    // Draw 4 small lines at 12, 3, 6, and 9 o'clock positions (0, 0.25, 0.5,
    // 0.75 of a full revolution). Each tick starts at the rim and goes 2.5px
    // inward.
    [0, 0.25, 0.5, 0.75].forEach(frac => {
      const angle = frac * 2 * Math.PI;
      ctx.beginPath();
      ctx.moveTo(cx + Math.sin(angle) * r,         cy - Math.cos(angle) * r);
      ctx.lineTo(cx + Math.sin(angle) * (r - 2.5), cy - Math.cos(angle) * (r - 2.5));
      ctx.strokeStyle = palette.accent;
      ctx.lineWidth = 1.5;
      ctx.lineCap = 'round';
      ctx.stroke();
    });

    // Helper: converts a clock-face fraction (0 = 12 o'clock, 1 = full turn)
    // and a hand length into an (x, y) canvas coordinate.
    // Uses sin for x and -cos for y because canvas y-axis points downward
    // while clock angles start at the top (12 o'clock) and go clockwise.
    const clockPt = (frac: number, len: number) => ({
      x: cx + Math.sin(frac * 2 * Math.PI) * len,
      y: cy - Math.cos(frac * 2 * Math.PI) * len,
    });

    // ── Step 4: Hour hand at 10 o'clock ──────────────────────────────────────
    // 10/12 of a full revolution = the 10 o'clock position. Length = 7px.
    const h = clockPt(10 / 12, 7);
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(h.x, h.y);
    ctx.strokeStyle = palette.textPrimary;
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.stroke();

    // ── Step 5: Minute hand at 2 o'clock ─────────────────────────────────────
    // 2/12 of a full revolution = the 2 o'clock position. Length = 10px.
    const m = clockPt(2 / 12, 10);
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(m.x, m.y);
    ctx.strokeStyle = palette.accent;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();

    // ── Step 6: Center dot ────────────────────────────────────────────────────
    ctx.beginPath();
    ctx.arc(cx, cy, 2.5, 0, Math.PI * 2);
    ctx.fillStyle = palette.accent;
    ctx.fill();

    // ── Inject the favicon into the page ─────────────────────────────────────
    // Find our dedicated dynamic-favicon <link> tag, or create one if it
    // doesn't exist yet. Never touch the static `/favicon.png` link — that
    // one must stay a real, crawlable URL for Google's favicon indexer.
    let link = document.querySelector<HTMLLinkElement>('link[data-dynamic-favicon="true"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/png';
      link.dataset.dynamicFavicon = 'true';
      document.head.appendChild(link);
    }
    link.href = canvas.toDataURL('image/png');

  // Re-run this effect whenever the theme or mode changes.
  }, [themeId, mode]);
}
